import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { restoreFocus } from '../a11y/focus';
import { AdminSavedViewsComponent } from './admin-saved-views.component';

/** One active filter, shown as a removable chip. */
export interface FilterChip {
  key: string;
  label: string;
}

/**
 * The list pages' filter card: a search slot on the left, the page's
 * controls beside it, and the active filters as removable chips with
 * "Clear all". Pages keep their filters in the URL; this is layout only.
 *
 *   <admin-filter-bar [chips]="activeChips" (remove)="clear($event)" (clearAll)="clearAll()">
 *     <ion-searchbar search …></ion-searchbar>
 *     <ion-select …></ion-select>
 *   </admin-filter-bar>
 *
 * Controls too big for a pill (e.g. date ranges) go in a popover opened from a
 * pill trigger: an `ion-item button` holding `.admin-filter-pill` (label and
 * value), and an `ion-popover` with `cssClass="admin-filter-popover"`.
 */
@Component({
  selector: 'admin-filter-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AdminSavedViewsComponent],
  // Unscoped so the compact pill styles reach the projected Ionic controls.
  encapsulation: ViewEncapsulation.None,
  template: `
    <section class="admin-filter-bar" aria-label="Filters">
      <div class="admin-filter-bar__row">
        <div class="admin-filter-bar__search"><ng-content select="[search]"></ng-content></div>
        <div class="admin-filter-bar__controls"><ng-content></ng-content></div>
      </div>
      @if (chips.length) {
        <div class="admin-filter-bar__chips">
          @for (chip of chips; track chip.key; let i = $index) {
            <button
              type="button"
              class="admin-filter-bar__chip"
              [attr.aria-label]="'Remove filter: ' + chip.label"
              (click)="removeChip(chip.key, i, $event)"
            >
              {{ chip.label }}
              <span class="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
          }
          <button type="button" class="admin-filter-bar__clear" (click)="clearChips($event)">Clear all</button>
        </div>
      }
      @if (views) {
        <admin-saved-views [key]="views" [active]="chips.length > 0"></admin-saved-views>
      }
    </section>
  `,
  styles: [
    `
      .admin-filter-bar {
        margin-bottom: 20px;
        padding: 14px 16px;
        border: 1px solid var(--cnt-line);
        border-radius: 1rem;
        background: #fff;
        box-shadow: var(--cnt-card-shadow);
      }
      .admin-filter-bar__row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 12px;
      }
      .admin-filter-bar__search {
        flex: 1 1 260px;
        min-width: 0;
      }
      .admin-filter-bar__search:empty {
        display: none;
      }
      .admin-filter-bar__controls {
        display: flex;
        flex: 1 1 auto;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
      }
      @media (max-width: 767px) {
        .admin-filter-bar__controls > * {
          flex: 1 1 100%;
        }
      }
      /* Searchbars (incl. user pickers) as the same cream pills as the selects. */
      .admin-filter-bar ion-searchbar.md,
      .admin-filter-bar ion-searchbar.ios {
        --background: rgba(247, 245, 236, 0.6);
        --border-radius: 9999px;
        --box-shadow: 0 0 0 1px var(--cnt-line-strong);
        padding: 0;
        min-height: 40px;
      }
      /* Compact pill selects and inputs. */
      .admin-filter-bar ion-item {
        --background: rgba(247, 245, 236, 0.6);
        --border-radius: 9999px;
        --padding-start: 14px;
        --inner-padding-end: 10px;
        --min-height: 40px;
        border: 1px solid var(--cnt-line-strong);
        border-radius: 9999px;
        font-size: 0.875rem;
        /* Room for the stacked label and the arrow even when nothing is picked. */
        min-width: 170px;
      }
      .admin-filter-bar ion-select {
        --padding-start: 0;
        min-height: 38px;
        font-size: 0.875rem;
      }
      /* Inputs (e.g. date ranges) match the selects. */
      .admin-filter-bar ion-input {
        --padding-top: 0;
        --padding-bottom: 0;
        min-height: 38px;
        font-size: 0.875rem;
      }
      /* Ionic's stacked inputs are 56px tall; the selects are 38px. */
      .admin-filter-bar ion-input.sc-ion-input-md-h.input-label-placement-stacked {
        min-height: 38px;
      }
      .admin-filter-bar ion-input.input-label-placement-stacked .input-wrapper.sc-ion-input-md {
        min-height: 38px;
      }
      .admin-filter-bar ion-input.input-label-placement-stacked .native-wrapper.sc-ion-input-md {
        min-height: 0;
        height: auto;
      }
      .admin-filter-bar admin-user-picker {
        flex: 1 1 220px;
        min-width: 200px;
      }
      .admin-filter-bar__chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--cnt-line);
      }
      .admin-filter-bar__chip {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 5px 8px 5px 12px;
        border: 1px solid rgba(var(--cnt-trinidad-rgb), 0.3);
        border-radius: 9999px;
        background: rgba(var(--cnt-trinidad-rgb), 0.06);
        color: var(--cnt-trinidad);
        font: inherit;
        font-size: 0.8125rem;
        font-weight: 600;
        cursor: pointer;
      }
      .admin-filter-bar__chip .material-symbols-outlined {
        font-size: 16px;
      }
      .admin-filter-bar__chip:hover {
        background: rgba(var(--cnt-trinidad-rgb), 0.12);
      }
      .admin-filter-bar__clear {
        margin-left: 4px;
        border: 0;
        background: transparent;
        color: var(--cnt-muted);
        font-family: var(--cnt-font-button);
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        cursor: pointer;
      }
      .admin-filter-bar__clear:hover {
        color: var(--cnt-trinidad);
      }
      .admin-filter-bar__chip:focus-visible,
      .admin-filter-bar__clear:focus-visible {
        outline: 2px solid var(--cnt-green);
        outline-offset: 2px;
      }
      /* A pill that opens a popover: a stacked label and its value, like the selects. */
      .admin-filter-pill {
        display: flex;
        flex: 1;
        flex-direction: column;
        min-width: 0;
        padding: 4px 0;
        line-height: 1.25;
      }
      .admin-filter-pill__label {
        font-size: 0.75rem;
        color: var(--cnt-muted);
      }
      .admin-filter-pill__value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .admin-filter-pill + .material-symbols-outlined {
        font-size: 18px;
        color: var(--cnt-muted);
      }
      /* The popover is rendered at the document root, outside .admin-filter-bar. */
      .admin-filter-popover {
        --width: auto;
        --max-width: calc(100vw - 32px);
      }
      .admin-filter-popover__body {
        display: grid;
        gap: 10px;
        padding: 14px 16px;
      }
      .admin-filter-popover__row {
        display: grid;
        grid-template-columns: 110px 150px auto 150px;
        align-items: center;
        gap: 8px;
        font-size: 0.875rem;
      }
      .admin-filter-popover__row ion-input {
        --padding-start: 12px;
        --padding-end: 8px;
        min-height: 36px;
        border: 1px solid var(--cnt-line-strong);
        border-radius: 9999px;
        background: rgba(247, 245, 236, 0.6);
        font-size: 0.875rem;
      }
      .admin-filter-popover__hint {
        margin: 0;
        font-size: 0.75rem;
        color: var(--cnt-muted);
      }
      .admin-filter-popover__actions {
        display: flex;
        justify-content: flex-end;
      }
      @media (max-width: 479px) {
        .admin-filter-popover__row {
          grid-template-columns: 1fr auto 1fr;
        }
        .admin-filter-popover__row > :first-child {
          grid-column: 1 / -1;
        }
      }
    `,
  ],
})
export class AdminFilterBarComponent {
  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  /** Turns on saved views, kept under this name (e.g. "reservations"). */
  @Input() views?: string;
  @Input() chips: FilterChip[] = [];
  /** The chip's key, to reset that one filter. */
  @Output() readonly remove = new EventEmitter<string>();
  @Output() readonly clearAll = new EventEmitter<void>();

  /** Removes a chip; focus moves to the chip that takes its place, the one before, or the first filter. */
  removeChip(key: string, index: number, event?: Event): void {
    const trigger = (event?.currentTarget as Element | null) ?? null;
    this.remove.emit(key);
    void restoreFocus(this.document, trigger, () => {
      const chips = this.chipButtons();
      return [chips[index], chips[index - 1], this.firstControl()];
    });
  }

  /** Clears every filter; focus moves to the first filter control. */
  clearChips(event?: Event): void {
    const trigger = (event?.currentTarget as Element | null) ?? null;
    this.clearAll.emit();
    void restoreFocus(this.document, trigger, () => [this.firstControl()]);
  }

  private chipButtons(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('.admin-filter-bar__chip'));
  }

  /** The search box, or else the first of the page's filter controls. */
  private firstControl(): Element | null {
    const el = this.host.nativeElement;
    return (
      el.querySelector('.admin-filter-bar__search ion-searchbar, .admin-filter-bar__search input') ??
      el.querySelector(
        '.admin-filter-bar__controls :is(ion-select, ion-searchbar, input, select, button, ion-item[button])',
      )
    );
  }
}
